<script setup lang="ts">
import { ref, PropType } from "vue";
import { Department, DirectLink, Participant, Project, ProjectRole, Trainer } from "../../../types/PersologOnlineTypes";
import { useI18n } from "vue-i18n";
import { ProfileAdministrationAPI } from "../../../api";
import ProjectInformation from "./../ProjectInformation.vue";
import { createParticipantAndLogActivity, saveDepartments, saveRoles } from "../../../utils/ProjectUtils";
import { useToast } from "primevue/usetoast";
import { isMobileView } from "../../../utils/GeneralUtils.ts";

const { t } = useI18n();

const emit = defineEmits(["valid", "update:visible"]);
const toast = useToast();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        default: null,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        required: true,
    },
    roles: {
        type: Array as PropType<ProjectRole[]>,
        default: () => [],
    },
    departments: {
        type: Array as PropType<Department[]>,
        default: () => [],
    },
});

const project = ref<Project | undefined>(props.project);
const participants = ref<Participant[]>(props.participants);
const roles = ref<ProjectRole[]>(props.roles);
const departments = ref<Department[]>(props.departments);

/**
 * Creates a new project in the backend.
 * Participants, Direct links, roles and departments are added if they are available.
 * It emits the update:visible event to notify the parent component that the project has been created
 * emits e.g.: this -> ConfigureProject -> ProjectView
 * @emits update:visible
 */
async function createProject() {
    try {
        const createdProject = await ProfileAdministrationAPI.createProject(project.value as Project).catch(() => {
            projectCreationErrorToast(t("project-creation.toasts.errorDetailsProject"));
        });
        if (!createdProject) {
            return;
        }

        if (participants.value && participants.value.length > 0) {
            const participantData = {
                participants: participants.value.map((p) => ({
                    ...p,
                    selectedProduct: createdProject.defaultProduct ?? createdProject.product ?? "",
                })),
            };
            await createParticipantAndLogActivity(createdProject.id as number, participantData.participants).catch(() => {
                projectCreationErrorToast(t("project-creation.toasts.errorDetailsParticipants"));
            });
        }

        if (props.directLink) {
            await ProfileAdministrationAPI.createDirectLink(createdProject.trainer as string, {
                project: createdProject,
                quantity: props.directLink.quantity,
                hash: props.directLink.hash,
                depleted: false,
                expiresAt: props.directLink.expiresAt ?? null,
            }).catch(() => {
                projectCreationErrorToast(t("project-creation.toasts.errorDetailsDirectLink"));
            });
        }

        if (roles.value && roles.value.length > 0) {
            await saveRoles(
                createdProject,
                // No id: these roles are new. An id makes the backend update that existing role instead.
                roles.value.map((role) => ({ role: role.role, name: role.name })),
            ).catch(() => {
                projectCreationErrorToast(t("project-creation.toasts.errorDetailsRoles"));
            });
        }

        if (departments.value && departments.value.length > 0) {
            await saveDepartments(createdProject, departments.value);
        }

        // Close the dialog and enable the next button
        emit("update:visible", createdProject);
        emit("valid", true);
    } catch (error) {
        console.error("Error creating project:", error);
        projectCreationErrorToast("");
    }
}

function projectCreationErrorToast(details: string = "") {
    toast.add({
        severity: "error",
        summary: t("project-creation.toasts.errorSummary"),
        detail: details,
        life: 8000,
    });
}

/**
 * validate is called by the parent component to check if the current step is valid
 * It is part of the ValidateableComponent interface (check types/PersologOnlineTypes.ts)
 * emits e.g.: this -> StepNavigation
 * @emits valid - false will disable the next button in the StepNavigation and after creating the project it will be enabled
 */
const validate = () => {
    emit("valid", false);
    createProject().then(() => {
        return true;
    });
};
defineExpose({ validate });
</script>

<template>
    <Card>
        <template #title
            ><span :id="`configure-preview-tab_project-title-${project?.id ?? 0}`">{{ project?.name }}</span></template
        >
        <template #subtitle>{{ project?.product?.toUpperCase() }}</template>
        <template #content>
            <ProjectInformation
                :project="project as Project"
                :direct-link="props.directLink"
                :trainer="trainer"
                :participants="participants"
                :with-label="true"
                :roles="roles"
                :departments="departments"
                :show-roles="true"
            />
        </template>
    </Card>
    <DataTable v-if="!isMobileView() && participants && participants?.length > 0" :value="participants">
        <Column field="email" :header="t('project-configuration.additionalRecipient.email')"></Column>
        <Column field="firstName" :header="t('project-configuration.additionalRecipient.firstName')"></Column>
        <Column field="name" :header="t('project-configuration.additionalRecipient.lastName')"></Column>
    </DataTable>
    <DataView
        data-key="id"
        v-if="isMobileView() && participants && participants?.length > 0"
        :value="participants"
        class="w-full"
        :pt="{
            header: {
                style: {
                    'margin-bottom': '0 !important',
                },
            },
            content: {
                style: {
                    'border-bottom': '1px solid var(--p-divider-border-color)',
                    'border-top': '1px solid var(--p-divider-border-color)',
                    'background-color': 'white !important',
                },
            },
        }"
    >
        <template #header>
            <span class="font-bold">{{ t("project-configuration.participants") }}</span>
        </template>
        <template #empty>
            <span>{{ t("project-details.participants.columns.empty") }}</span>
        </template>
        <template #list="slotProps">
            <div v-for="(participant, index) in slotProps.items" :key="participant.id">
                <div class="ml-2 my-4 flex flex-wrap gap-4">
                    <div class="grid grid-cols-12 gap-4 w-full">
                        <div v-if="participant.firstName !== '' || participant.name !== ''" class="font-bold col-span-6">
                            {{ participant.firstName }} {{ participant.name }}
                        </div>
                        <div v-if="participant.firstName === '' && participant.name === ''" class="font-bold col-span-6">
                            {{ participant.email }}
                        </div>
                    </div>
                    <div v-if="participant.firstName !== '' && participant.name !== ''" class="grid grid-cols-12 gap-4">
                        <div class="col-span-12">
                            {{ participant.email }}
                        </div>
                    </div>
                </div>
                <Divider v-if="Number(index) + 1 < participants.length" class="mr-4" />
            </div>
        </template>
    </DataView>
</template>
